<template>
  <teleport to="body">
    <transition name="fade">
      <div
        v-if="isOpen"
        class="overlay"
        role="dialog"
        aria-modal="true"
        @click.self="closeOnBackdrop ? close() : null"
      >
        <div
          class="pop-up"
          :class="[sizeClass, { claseConOverflow: noScroll }]"
          @click.stop
        >
          <div class="inline-title modal-header-like">
            <div>
              <h3 class="modal-title-like">{{ title }}</h3>
            </div>
            <button
              v-if="showCloseIcon"
              @click="close"
              class="btn btn-danger close-button"
              aria-label="Cerrar"
              title="Cerrar"
            >
              &#10005;
            </button>
          </div>

          <section class="modal-body-like">
            <slot />
          </section>

          <div class="modal-footer-like">
            <slot name="footer">
              <button class="btn btn-outline-light" @click="close">Cerrar</button>
            </slot>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script setup>
import {
  computed,
  onMounted,
  onUnmounted,
  watch,
  defineProps,
  defineEmits,
  defineOptions,
} from "vue";

defineOptions({ name: "CustomModal" });

const props = defineProps({
  modelValue: { type: Boolean, required: true },
  title: { type: String, default: "" },
  closeOnBackdrop: { type: Boolean, default: true },
  showCloseIcon: { type: Boolean, default: true },
  size: {
    type: String,
    default: "md",
    validator: (v) => ["sm", "md", "lg", "xl", "2xl"].includes(v),
  },
  noScroll: { type: Boolean, default: false },
});

const emit = defineEmits(["update:modelValue", "close", "open"]);

const isOpen = computed({
  get: () => props.modelValue,
  set: (v) => emit("update:modelValue", v),
});

const sizeClass = computed(() => `size-${props.size}`);

const close = () => {
  if (!isOpen.value) return;
  isOpen.value = false;
  emit("close");
};

const onKey = (e) => {
  if (e.key === "Escape") close();
};

watch(isOpen, (v) => {
  if (typeof document !== "undefined") {
    document.body.style.overflow = v ? "hidden" : "";
  }
  if (v) emit("open");
});

onMounted(() => window.addEventListener("keydown", onKey));
onUnmounted(() => {
  window.removeEventListener("keydown", onKey);
  if (typeof document !== "undefined") document.body.style.overflow = "";
});
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active { transition: opacity 0.18s ease; }
.fade-enter-from,
.fade-leave-to { opacity: 0; }

.overlay {
  position: fixed;
  inset: 0;
  background: rgba(78, 78, 78, 0.404);
  z-index: 9999;
  display: grid;
  place-items: center;
  padding: 1rem;
}

.pop-up {
  padding: 2rem 3rem;
  position: relative;
  background-color: #141218;
  border-radius: 20px;
  border: 1px solid #f4641652;
  box-sizing: border-box;
  min-width: 55vw;
  min-height: 60vh;
  max-width: 90vw;
  max-height: 85vh;
  overflow: auto;
}

.claseConOverflow { overflow: hidden; }

.inline-title {
  width: 100%;
  align-items: center;
  display: flex;
  justify-content: space-between;
}

.close-button {
  margin: 0;
  font-size: 2rem;
  background: none;
  border: none;
  cursor: pointer;
}

hr {
  width: 100%;
  border: none;
  border: 3px solid white;
  margin: 1em 0;
  padding: 0;
}

*::-webkit-scrollbar,
body::-webkit-scrollbar { height: 8px; width: 8px; }
*::-webkit-scrollbar-thumb,
body::-webkit-scrollbar-thumb { background: #ffffff41; }
.pop-up::-webkit-scrollbar { width: 8px; }

.modal-title-like { margin: 0; }

.modal-footer-like {
  margin-top: 1rem;
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}

.size-sm { max-width: 24rem; }
.size-md { max-width: 32rem; }
.size-lg { max-width: 48rem; }
.size-xl { max-width: 64rem; }
.size-2xl { max-width: 80rem; }

@media (max-width: 480px) {
  .pop-up {
    padding: 1rem 1.25rem;
    min-width: 90vw;
    min-height: 60vh;
  }
}
</style>
